import React, { useContext } from 'react';
import { styled, css } from 'styled-components';
import Button from '@ingka/button';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { mediaQueries } from '../styles/mediaQueries';
import colors from '../styles/colors';
import { getPath, getPrevPath } from '../helpers/urlHelper';
import { useStateSelector } from '../store/';
import { selectAppOperation } from '../reducers/selectors';
import { PageContext, ViewContext } from '../context';

interface FollowUpAppFooterProps {
  prevVisible?: boolean;
  nextVisible?: boolean;
  nextDisabled?: boolean;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
  isLoading?: boolean;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: auto;
  max-width: 500px;
`;

const StyledDivider = styled.div`
  margin: 1.5rem 0 1rem;
  border-top: 1px solid ${colors.neutral.grey200};

  ${mediaQueries.landscapePhoneUp} {
    margin: 2.5rem 0 1rem;
  }

  ${mediaQueries.desktopUp} {
    margin: 2.5rem 0;
  }
`;

const buttonStyle = css`
  width: 48%;
  max-width: 234px;
`;

const PrevButton = styled(Button)`
  ${buttonStyle};
  justify-content: center;
`;

const NextButton = styled(Button)`
  ${buttonStyle};
  margin-left: auto;
`;

const FollowUpAppFooter: React.FC<FollowUpAppFooterProps> = ({
  prevVisible,
  nextVisible,
  nextDisabled,
  prevAriaLabel,
  nextAriaLabel,
  isLoading,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const view = useContext(ViewContext);
  const page = useContext(PageContext);
  const operation = useStateSelector(selectAppOperation);
  const isTimeslotsPage = page === 'followup-timeslots';
  const search = window.location.search;

  const handlePrevious = () => {
    navigate({ pathname: getPrevPath(operation, view), search });
  };

  const handleNext = () => {
    navigate({ pathname: getPath(operation, 'confirm', view), search });
  };

  return (
    <>
      <StyledDivider />
      <ButtonContainer>
        {prevVisible && (
          <PrevButton
            disabled={isLoading}
            onClick={handlePrevious}
            text={
              isTimeslotsPage
                ? t('pages.followup.timeslots.updateContact')
                : t('components.appFooter.prev')
            }
            type="tertiary"
            data-cy="previous"
            aria-label={prevAriaLabel}
          />
        )}
        {nextVisible && (
          <NextButton
            htmlType="button"
            onClick={handleNext}
            disabled={nextDisabled}
            loading={isLoading}
            text={
              isTimeslotsPage
                ? t('pages.followup.timeslots.bookFollowUp')
                : t('pages.followup.contact.bookFollowUp')
            }
            type="emphasised"
            id="nextbutton"
            data-cy="next-button"
            aria-label={nextAriaLabel}
          />
        )}
      </ButtonContainer>
    </>
  );
};

export default FollowUpAppFooter;
